import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import { Mail, Phone, Building2, Globe2, Check, X, Clock, RefreshCw, Zap } from "lucide-react";
import { formatDateTime } from "@/lib/format";

const STATUS_STYLES = {
    new: "bg-orange-50 text-[#F26322]",
    contacted: "bg-sky-50 text-sky-700",
    converted: "bg-emerald-50 text-emerald-700",
    spam: "bg-slate-100 text-slate-500",
};

const PRETTY_ROLE = {
    owner: "Owner", general_manager: "General Manager",
    operations_manager: "Operations Manager", front_desk_manager: "Front Desk Manager",
    it: "IT", consultant: "Consultant", other: "Other",
};

const PRETTY_TYPE = {
    hotel: "Hotel", boutique_hotel: "Boutique Hotel", hostel: "Hostel",
    apartments: "Apartments", villa: "Villa", bed_and_breakfast: "Bed & Breakfast",
    resort: "Resort", guest_house: "Guest House", other: "Other",
};

export default function SuperDemoRequests() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/superadmin/demo-requests");
            setRows(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.post(`/superadmin/demo-requests/${id}/status?status=${status}`);
            toast.success(`Marked as ${status}`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const approveDemo = async (r) => {
        if (!window.confirm(
            `Approve "${r.property_name}"?\n\n` +
            `A new hotel workspace will be created with:\n` +
            `• Slug: auto-generated from property name\n` +
            `• Admin: ${r.email}\n` +
            `• Trial: 14 days\n` +
            `• Welcome email with setup link\n\n` +
            `Continue?`
        )) return;
        try {
            const { data } = await api.post(`/superadmin/demo-requests/${r.id}/approve`);
            if (data.idempotent_hit) {
                toast.info(`Already converted — hotel /${data.slug}`);
            } else {
                toast.success(`Workspace created: /${data.slug}` +
                              (data.welcome_email_sent ? " · welcome email sent" : " · email queued"));
            }
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <>
            <Topbar title={t("super_admin.demo_requests.title")} subtitle={t("super_admin.demo_requests.subtitle")} />
            <div className="flex-1 overflow-auto p-6" data-testid="super-demo-requests">
                <div className="akm-card p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <div className="akm-section-title">
                            {rows.length} request{rows.length === 1 ? "" : "s"}
                        </div>
                        <button onClick={load} disabled={loading} className="akm-btn-ghost h-8 text-[12px] disabled:opacity-50">
                            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                        </button>
                    </div>
                    {rows.length === 0 ? (
                        <div className="py-12 text-center text-[13px] text-muted-foreground">
                            No demo requests yet. Submissions to /signup will appear here.
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {rows.map((r) => (
                                <li key={r.id} className="py-4" data-testid={`demo-row-${r.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <div className="text-[14px] font-semibold text-slate-900">
                                                    {r.first_name} {r.last_name}
                                                </div>
                                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}>
                                                    {r.status || "new"}
                                                </span>
                                                <span className="text-[11px] text-muted-foreground">
                                                    <Clock size={10} className="inline" /> {formatDateTime(r.created_at)}
                                                </span>
                                            </div>
                                            <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-[12.5px] sm:grid-cols-2 lg:grid-cols-4">
                                                <div className="flex items-center gap-1.5 text-slate-600"><Mail size={12} className="text-slate-400" /><a href={`mailto:${r.email}`} className="hover:text-[#F26322]">{r.email}</a></div>
                                                <div className="flex items-center gap-1.5 text-slate-600"><Phone size={12} className="text-slate-400" /><a href={`tel:${r.phone}`} className="hover:text-[#F26322]">{r.phone}</a></div>
                                                <div className="flex items-center gap-1.5 text-slate-600"><Building2 size={12} className="text-slate-400" />{r.property_name} · {PRETTY_TYPE[r.property_type] || r.property_type}</div>
                                                <div className="flex items-center gap-1.5 text-slate-600"><Globe2 size={12} className="text-slate-400" />{r.country} — {PRETTY_ROLE[r.job_role] || r.job_role}</div>
                                            </div>
                                            {r.heard_about && (
                                                <div className="mt-2 text-[12px] text-slate-500">
                                                    <span className="font-semibold text-slate-600">Heard about us:</span> {r.heard_about}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {r.status !== "converted" && r.status !== "spam" && (
                                                <button
                                                    onClick={() => approveDemo(r)}
                                                    data-testid={`demo-approve-${r.id}`}
                                                    className="rounded-lg bg-[#F26322] px-3 py-1 text-[11px] font-bold text-white hover:bg-[#d94d10]"
                                                >
                                                    <Zap size={11} className="inline -mt-0.5" /> Approve & create workspace
                                                </button>
                                            )}
                                            {r.status === "converted" && r.converted_hotel_id && (
                                                <a
                                                    href={`/admin/hotels/${r.converted_hotel_id}`}
                                                    className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
                                                    data-testid={`demo-open-hotel-${r.id}`}
                                                >
                                                    <Check size={10} className="inline" /> View hotel
                                                </a>
                                            )}
                                            {["new", "contacted", "converted", "spam"].map((s) => (
                                                <button
                                                    key={s}
                                                    onClick={() => setStatus(r.id, s)}
                                                    disabled={r.status === s}
                                                    data-testid={`demo-set-${s}-${r.id}`}
                                                    className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition ${
                                                        r.status === s
                                                            ? "cursor-not-allowed bg-slate-100 text-slate-400"
                                                            : "border border-slate-200 text-slate-600 hover:border-[#F26322] hover:text-[#F26322]"
                                                    }`}
                                                >
                                                    {s === "converted" && <Check size={10} className="inline" />}
                                                    {s === "spam" && <X size={10} className="inline" />}
                                                    {" "}{s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </>
    );
}
